const API_BASE = 'https://back-end-artcustom.onrender.com'
const TOKEN_KEY = 'artcustom_auth_token'

export type LoginCredentials = {
  email: string
  senha: string
}

type ApiEnvelope = {
  success?: boolean
  message?: string
  data?: unknown
  Data?: unknown
  token?: string
  Token?: string
  accessToken?: string
  AccessToken?: string
  error?: string
}

/** Remove espaços, aspas e prefixo Bearer para guardar só o JWT. */
export function sanitizeToken(raw: string | null | undefined): string | null {
  if (raw == null) return null

  let token = String(raw).trim()
  if (!token) return null

  if (
    (token.startsWith('"') && token.endsWith('"')) ||
    (token.startsWith("'") && token.endsWith("'"))
  ) {
    token = token.slice(1, -1).trim()
  }

  const bearerPrefix = /^bearer\s+/i
  while (bearerPrefix.test(token)) {
    token = token.replace(bearerPrefix, '').trim()
  }

  return token || null
}

function extractToken(payload: ApiEnvelope): string | null {
  const nested = payload.data ?? payload.Data

  if (typeof nested === 'string') {
    return sanitizeToken(nested)
  }

  if (nested && typeof nested === 'object') {
    const inner = nested as Record<string, unknown>
    const fromNested =
      inner.token ?? inner.Token ?? inner.accessToken ?? inner.AccessToken
    const cleaned = sanitizeToken(
      typeof fromNested === 'string' ? fromNested : null,
    )
    if (cleaned) return cleaned
  }

  const fromRoot = payload.token ?? payload.Token ?? payload.accessToken ?? payload.AccessToken
  return sanitizeToken(typeof fromRoot === 'string' ? fromRoot : null)
}

/** Valor do header Authorization (Bearer + JWT), igual Postman/Swagger. */
export function getAuthorizationHeader(): string | null {
  const token = sanitizeToken(getToken())
  if (!token) return null
  return `Bearer ${token}`
}

export async function login(credentials: LoginCredentials): Promise<void> {
  const response = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: credentials.email,
      senha: credentials.senha,
    }),
  })

  const data = (await response.json().catch(() => ({}))) as ApiEnvelope

  if (!response.ok) {
    const message =
      typeof data.message === 'string'
        ? data.message
        : typeof data.error === 'string'
          ? data.error
          : 'E-mail ou senha inválidos.'
    throw new Error(message)
  }

  const token = extractToken(data)
  if (!token) {
    throw new Error('Login sem token na resposta da API.')
  }

  localStorage.setItem(TOKEN_KEY, token)
}

export function getToken(): string | null {
  return sanitizeToken(localStorage.getItem(TOKEN_KEY))
}

export function isAuthenticated(): boolean {
  return getToken() !== null
}

export function logout(): void {
  localStorage.removeItem(TOKEN_KEY)
}
